
import React from 'react';
import './jobDetailFilters.scss';
import Button from '../../../../components/button/button';
import Dropdown from '../../../../components/dropdown/dropdown';
import Filter, { RadioButtonOption } from '../../../../components/filter/filter';
import { MultiChipSelectOption } from '../../../../components/filters/multiChipSelect/multiChipSelect';
import { __capitalizeFirstLetter } from '../../../../utils/utils';
import { ActiveFilter } from '../../../crawler.interface';

export interface JobDetailFilters {
    activeFilters: ActiveFilter[];
    dropdownRef: React.RefObject<HTMLDivElement>;
    filterOptions: string[];
    isFilterDropdownOpen: boolean;
    domainOptions: string[];
    phraseOptions: string[];
    submitFilterValue: (filter: ActiveFilter) => void;
}
function JobDetailFilters({ activeFilters = [], dropdownRef, filterOptions, isFilterDropdownOpen, domainOptions, phraseOptions, submitFilterValue }: JobDetailFilters) {

    const [displayedFilter, setDisplayedFilter] = React.useState<string>('');
    const [selectedPageTypeChips, setPageTypeChips] = React.useState<MultiChipSelectOption[]>([]);
    const [selectedConfidenceLevel, setConfidenceLevel] = React.useState<MultiChipSelectOption>();

    React.useEffect(() => {
        init();
    }, []);

    function init() {
        assignSelectedChips();
    }

    function assignSelectedChips() {
        const pageTypeFilterIndex = activeFilters.findIndex((filter: ActiveFilter) => filter.name === 'page_type');
        if (pageTypeFilterIndex !== -1) {
            // Default for Page Type
            const pageTypeFilter = activeFilters[pageTypeFilterIndex];
            const values = pageTypeFilter.value.split('|');
            const chips: MultiChipSelectOption[] = [];
            values.map((val: string) => {
                const label = __capitalizeFirstLetter(val);
                chips.push({ name: val, value: val, label: label });
            });
            setPageTypeChips([...chips]);
            // Default for Confidence Level
            const confidenceLevel = pageTypeFilter.confidenceLevel;
            if (confidenceLevel) {
                const confidenceLabel = __capitalizeFirstLetter(confidenceLevel);
                setConfidenceLevel({ name: confidenceLevel, value: confidenceLevel, label: confidenceLabel });
            }
        }
    }

    function addFilter(filterType: string) {
        setDisplayedFilter(filterType);
    }

    const pageTypeMultiChipOptions: MultiChipSelectOption[] = [
        { name: 'any', value: 'any', label: 'Any' },
        { name: 'about', value: 'about', label: 'About Us' },
        { name: 'article', value: 'article', label: 'Article' },
        { name: 'blog', value: 'blog', label: 'Blog' },
        { name: 'checkout', value: 'checkout', label: 'Checkout' },
        { name: 'directory', value: 'directory', label: 'Directory' },
        { name: 'email', value: 'email', label: 'Email' },
        { name: 'faq', value: 'faq', label: 'FAQ' },
        { name: 'financing', value: 'financing', label: 'Financing' },
        { name: 'home', value: 'home', label: 'Home' },
        { name: 'multi card', value: 'multi card', label: 'Multi Card' },
        { name: 'none', value: 'none', label: 'None' },
        { name: 'privacy', value: 'privacy', label: 'Privacy' },
        { name: 'product', value: 'product', label: 'Product' },
        { name: 'single card', value: 'single card', label: 'Single Card' },
        { name: 'support', value: 'support', label: 'Support' },
        { name: 'terms', value: 'terms', label: 'Terms of Use' },

    ];

    const selectedPageRadioButtons: RadioButtonOption[] = [
        { label: 'Selected', value: 'true', checked: false },
        { label: 'Unselected', value: 'false', checked: false },
        { label: 'Both', value: 'clear-filter', checked: true }
    ];

    const selectedCheckboxValues = React.useMemo(() => {
        return activeFilters.find((filter: ActiveFilter) => filter.name === 'phrases')?.value;
    }, [activeFilters]);

    let currentFilter;
    switch (displayedFilter) {
        case 'Selected':
            currentFilter =
                <Filter
                    title="Selected"
                    label="Selected Pages"
                    name="selected"
                    filterType="radio"
                    defaultRadioSelection={{ label: 'Both', value: 'clear-filter', checked: true }}
                    radioButtonOptions={selectedPageRadioButtons}
                    onSubmit={submitFilterValue}
                    closeFilter={() => setDisplayedFilter('')}
                />;
            break;
        case 'URL':
            currentFilter =
                <Filter
                    title="URL"
                    label="URL contains:"
                    name="uri"
                    filterType="text"
                    onSubmit={submitFilterValue}
                    closeFilter={() => setDisplayedFilter('')}
                />;
            break;
        case 'Traffic Source':
            currentFilter =
                <Filter
                    title="Traffic Source"
                    label="Traffic Source contains:"
                    name="traffic_source"
                    filterType="text"
                    onSubmit={submitFilterValue}
                    closeFilter={() => setDisplayedFilter('')}
                />;
            break;
        case 'Domain':
            currentFilter =
                <Filter
                    title="Domain"
                    label="Search for Domain"
                    name="domain"
                    filterType="type-ahead"
                    typeAheadOptions={domainOptions}
                    onSubmit={submitFilterValue}
                    closeFilter={() => setDisplayedFilter('')}
                />;
            break;
        case 'Page Type':
            currentFilter =
                <Filter
                    title="Page Type"
                    label="Filter by Page Type"
                    name="page_type"
                    filterType="page-type-filter"
                    defaultSelectedChips={selectedPageTypeChips}
                    defaultConfidenceLevel={selectedConfidenceLevel}
                    multiChipOptions={pageTypeMultiChipOptions}
                    onSubmit={submitFilterValue}
                    closeFilter={() => setDisplayedFilter('')}
                />;
            break;
        case 'Phrase':
            currentFilter =
                <>
                    {
                        phraseOptions.length > 0 ?
                            <Filter
                                title="Phrases"
                                label="Select Phrases"
                                name="phrases"
                                filterType="checkbox"
                                checkboxOptions={phraseOptions}
                                defaultCheckedOptions={selectedCheckboxValues}
                                onSubmit={submitFilterValue}
                                closeFilter={() => setDisplayedFilter('')}
                            />
                            :
                            <span className="no-phrases">No phrases available</span>
                    }
                </>;
            break;
        default:
            currentFilter = <span style={{ color: 'red' }}>Invalid Filter Selected</span>;
    }

    return (
        <Dropdown isOpen={isFilterDropdownOpen} hasSubmit={false} ref={dropdownRef}>
            <div data-testid="job-detail-filter">
                {displayedFilter?.length < 1 ?
                    <div className="dropdown-display-options">
                        {filterOptions.map((option: string) => (
                            <span key={option} className={"dropdown-option " + option} data-testid={"filter-option-" + option.split(' ').join('-')} onClick={() => addFilter(option)}>
                                <Button variant="submit-filter-button" cyTestId={option + "-button"} >{option}</Button>
                            </span>
                        ))}
                    </div>
                    :
                    <>
                        {currentFilter}
                    </>
                }
            </div>
        </Dropdown>
    );
}

export default JobDetailFilters;

